/**
 * Picking the county a County plan covers.
 *
 * Shown in two situations, which are not the same thing:
 *
 * * **A subscription with no county yet.** The plan is real, the data routes
 *   refuse to answer until one is chosen, and this is the whole screen.
 * * **Moving an existing county.** Allowed once every thirty days, and the
 *   server is the one that decides — this only tries to keep someone from
 *   spending the move by accident.
 */

import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { County, User } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { Link } from "../router";
import { fmt } from "../lib/format";
import { SIGNAL_TOTAL, countyDepth } from "../lib/countyDepth";

interface Props {
  /** Shown above the choices. */
  title: string;
  onChosen?: (user: User) => void;
  onCancel?: () => void;
}

export function CountyChooser({ title, onChosen, onCancel }: Props) {
  const { user, setUser } = useAuth();
  const [counties, setCounties] = useState<County[]>([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    api
      .counties(controller.signal)
      .then((response) =>
        setCounties(response.counties.filter((c) => c.status === "live")),
      )
      .catch(() => setError("Couldn't load the county list."));
    return () => controller.abort();
  }, []);

  const held = user?.counties.held ?? null;
  const locked = user?.counties.next_change_at ?? null;

  async function choose(fips: string) {
    setError("");
    setBusy(fips);
    try {
      const updated = await api.chooseCounty(fips);
      setUser(updated);
      onChosen?.(updated);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Couldn't set that county.",
      );
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="chooser">
      <div className="eyebrow">Your plan covers one county</div>
      <h2>{title}</h2>

      {locked && (
        <p className="sub">
          You can move your county again on{" "}
          <b>{new Date(locked).toLocaleDateString()}</b>. The State plan covers
          every county at once.
        </p>
      )}

      {error && (
        <div className="err" role="alert">
          {error}
        </div>
      )}

      <ul className="chooser-list">
        {counties.map((county) => (
          <li key={county.fips}>
            <button
              className={`chooser-option${county.fips === held ? " is-held" : ""}`}
              disabled={Boolean(busy) || Boolean(locked && county.fips !== held)}
              onClick={() => choose(county.fips)}
            >
              <span className="chooser-name">
                {county.name} County
                {/* Depth, beside the name rather than after the counts: the
                    counts say how much is here and this says what kind. A
                    county can hold 27,000 parcels and no case record, and
                    volume is the number that hides that. */}
                <span
                  className={`chip ${
                    countyDepth(county.signals).hasCases ? "chip-cases" : "chip-roll"
                  }`}
                >
                  {countyDepth(county.signals).badge}
                </span>
              </span>
              {/* What they would actually be buying access to, so the choice
                  is made on coverage rather than on familiarity. */}
              <span className="chooser-counts">
                {county.parcels > 0 && `${fmt(county.parcels)} parcels`}
                {county.parcels > 0 && county.buildings > 0 && " · "}
                {county.buildings > 0 && `${fmt(county.buildings)} buildings`}
                {county.parcels === 0 && county.buildings === 0 && "no data yet"}
                {" · "}
                {countyDepth(county.signals).signals} of {SIGNAL_TOTAL} signals
              </span>
              {county.fips === held && <span className="chooser-held">Current</span>}
              {busy === county.fips && <span className="chooser-held">Saving…</span>}
            </button>
          </li>
        ))}
      </ul>

      <p className="sub">
        Not sure? The <Link to="/coverage">coverage map</Link> shows what each
        county holds. You can move to another county once every 30 days.
      </p>

      {onCancel && (
        <button className="linkbtn" onClick={onCancel}>
          Cancel
        </button>
      )}
    </div>
  );
}
